import * as React from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Modal,
  IconButton,
  Chip,
  Tooltip,
  Autocomplete,
  TablePagination,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import Header from "../Includes/Header";
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useNavigate } from "react-router-dom";
import { generatePDFInvoice } from "../../Utils/pdfUtils";
import {
  retrieveCheckindata,
  GetTotalWorkHour,
  Manualcheckout,
  GenerateSalary,
  StoreBiometric
} from "../Apihelper/AttendanceHelper";
import { AutocompleteNonUser } from "../Apihelper/ApiOfferPackage";
import AppToast from '../../components/Toast/AppToast';

// "9:00 AM" → minutes since midnight
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const m = String(timeStr).trim().match(/^(\d{1,2}):(\d{2})\s*([AP]M)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const period = m[3]?.toUpperCase();
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return h * 60 + min;
};

// Slot string like "9:00 AM - 1:00 PM" → { startMin, endMin }
const slotWindow = (slotStr) => {
  if (!slotStr) return null;
  const normalized = String(slotStr).replace(/[–—]/g, '-');
  const parts = normalized.split(/\s*-\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  const startMin = parseTimeToMinutes(parts[0]);
  const endMin = parseTimeToMinutes(parts[1]);
  if (startMin == null || endMin == null || endMin <= startMin) return null;
  return { startMin, endMin };
};

// Hours of one full working day for a staff row: morning slot + evening slot (or the one slot they have).
const fullDaySlots = (row) => {
  const parts = [
    { label: 'Morning', window: slotWindow(row?.morning_slot) },
    { label: 'Evening', window: slotWindow(row?.evening_slot) },
  ].filter((p) => p.window).map((p) => ({ label: p.label, hours: (p.window.endMin - p.window.startMin) / 60 }));
  const total = parts.reduce((sum, p) => sum + p.hours, 0);
  return { parts, total: Math.round(total * 100) / 100 };
};

const formatHours = (h) => `${Math.round(h * 100) / 100} h`;

const formatMinutesToTime = (min) => {
  if (min == null) return null;
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

// For a split-shift employee, break the day's punches into Morning/Evening
// sessions instead of showing one collapsed check_in/check_out for the day.
const getSessionsForRow = (row) => {
  const namedSlots = [
    { label: 'Morning', window: slotWindow(row.morning_slot) },
    { label: 'Evening', window: slotWindow(row.evening_slot) },
  ].filter((s) => s.window);

  if (namedSlots.length <= 1) {
    return [{ label: null, checkIn: row.check_in || null, checkOut: row.check_out || null }];
  }

  const sessions = [];
  let openIn = null;
  for (const p of row.punches || []) {
    if (p.type === 'in') {
      if (openIn) sessions.push({ in: openIn, out: null });
      openIn = p.time;
    } else if (p.type === 'out' && openIn) {
      sessions.push({ in: openIn, out: p.time });
      openIn = null;
    }
  }
  if (openIn) sessions.push({ in: openIn, out: null });
  if (sessions.length === 0 && row.check_in) {
    sessions.push({ in: row.check_in, out: row.check_out });
  }

  return namedSlots.map(({ label, window }) => {
    const session = sessions.find((s) => {
      const m = parseTimeToMinutes(s.in);
      return m !== null && m >= window.startMin - 30 && m <= window.endMin;
    });
    if (!session) return { label, checkIn: null, checkOut: null };
    const checkOut = session.out || formatMinutesToTime(window.endMin);
    const isEstimated = !session.out;
    return { label, checkIn: session.in, checkOut, isEstimated };
  });
};

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 'bold',
    borderBottom: 'none',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f9f9f9',
  },
  '&:hover': {
    backgroundColor: 'rgba(31, 122, 84, 0.08)',
    cursor: 'pointer',
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const WorkHoursModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  width: '90%',
  maxWidth: 500,
  maxHeight: '90vh',
  overflow: 'auto',
}));

function AttendanceEmp({ onTotalChange }) {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // States
  const [loading, setLoading] = React.useState(false);
  const [loadingFetch, setLoadingFetch] = React.useState(false);
  const [fetchdatarow, setFetchdatarow] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalCount, setTotalCount] = React.useState(0);
  const [options, setOptions] = React.useState([]);
  const [openToast, setOpenToast] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");
  const [workHours, setWorkHours] = React.useState(null);
  const [openWork, setOpenWork] = React.useState(false);
  const [hoursPerDay, setHoursPerDay] = React.useState(8);
  const [salaryPerDay, setSalaryPerDay] = React.useState("");
  const [currentEmpId, setCurrentEmpId] = React.useState(null);
  const [fullDay, setFullDay] = React.useState({ parts: [], total: 0 }); // slot hours that make one full day for the person being paid

  // Handlers
  const handleSnackbarOpen = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenToast(true);
  };

  const handleToastClose = () => setOpenToast(false);

  const fetchData = async (value = "", newPage = 0, newRowsPerPage = 10) => {
    setLoading(true);
    try {
      const userData = localStorage.getItem("userdata");
      const userDataObj = JSON.parse(userData);
      const userToken = userDataObj.token;
      const members = "employees";

      const res = await retrieveCheckindata(
        userToken,
        "",
        members,
        value,
        newRowsPerPage,
        newPage * newRowsPerPage
      );

      if (res.code === 200) {
        if (res.data?.length > 0) {
          setFetchdatarow(res.data);
        } else {
          setFetchdatarow([]);
          handleSnackbarOpen("No Record Found", "warning");
        }
        const count = res.total_count ?? 0;
        setTotalCount(count);
        if (onTotalChange) onTotalChange(count);
      }
    } catch (error) {
      console.error(error);
      handleSnackbarOpen("Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData("", newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchData("", 0, newRowsPerPage);
  };

  const handleCheckout = async (id) => {
    try {
      const userData = JSON.parse(localStorage.getItem("userdata"));
      const response = await Manualcheckout(userData.token, id);

      handleSnackbarOpen(response.message, response.code == "200" ? "success" : "error");
      if (response.code == "200") fetchData();
    } catch (error) {
      console.error("Checkout Error:", error);
      handleSnackbarOpen("Checkout failed", "error");
    }
  };

  const handleRefresh = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userdata"));
      const response = await StoreBiometric(userData.token);

      handleSnackbarOpen(response.message, response.code == "200" ? "success" : "error");
      if (response.code == "200") fetchData();
    } catch (error) {
      console.error("Refresh Error:", error);
      handleSnackbarOpen("Refresh failed", "error");
    }
  };

  const fetchWorkHours = async (userId) => {
    setLoadingFetch(true);
    setCurrentEmpId(userId);
    // One full day = all of the person's slots (morning + evening, or the single slot); fall back to 8 if none are saved.
    const slotInfo = fullDaySlots(fetchdatarow.find((r) => String(r.user_id) === String(userId)));
    setFullDay(slotInfo);
    setHoursPerDay(slotInfo.total > 0 ? slotInfo.total : 8);
    try {
      const userData = JSON.parse(localStorage.getItem("userdata"));
      const response = await GetTotalWorkHour(userData.token, userId);
      
      if (response.code == 200) {
        setWorkHours(response.total_work_hours);
        handleSnackbarOpen(response.message, "success");
      } else {
        handleSnackbarOpen(response.message, "error");
      }
    } catch (error) {
      console.error("Error fetching work hours:", error);
      handleSnackbarOpen("Failed to fetch work hours", "error");
    } finally {
      setLoadingFetch(false);
      setOpenWork(true);
    }
  };

  const handleGenerateSalary = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("userdata"));
      const result = await GenerateSalary(
        userData.token,
        currentEmpId,
        workHours,
        hoursPerDay,
        salaryPerDay,
      );

      if (result.code === "200") {
        handleSnackbarOpen(result.message, "success");
        generatePDFInvoice(result.data);
        setOpenWork(false);
      } else {
        handleSnackbarOpen(result.message, "error");
      }
    } catch (error) {
      console.error("Error generating salary:", error);
      handleSnackbarOpen("Failed to generate salary", "error");
    }
  };

  // Effects
  React.useEffect(() => {
    const userData = localStorage.getItem("userdata");
    if (!userData) navigate('/');
    else {
      fetchData("", page, rowsPerPage);
    }
  }, []);

  return (
      <Box sx={{
        width: '100%',
        p: { xs: 1, sm: 2, md: 3 },
        bgcolor: '#f3f4f6'
      }}>
        {/* Header Section */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: appColors.leaf }}>
            Employees Attendance
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/salary-generation')}
              startIcon={<AccessTimeIcon />}
              fullWidth={isMobile}
              sx={{
                borderColor: appColors.leaf,
                color: appColors.leaf,
                '&:hover': { bgcolor: 'rgba(31,122,84,0.08)' },
              }}
            >
              Salary Generation
            </Button>
            <Button
              variant="contained"
              onClick={handleRefresh}
              startIcon={<RefreshIcon />}
              fullWidth={isMobile}
              sx={{
                bgcolor: appColors.leaf,
                '&:hover': { bgcolor: appColors.pine },
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <Autocomplete
            freeSolo
            disableClearable
            options={options}
            onInputChange={(_, value) => {
              if (value.length >= 3) {
                const userData = JSON.parse(localStorage.getItem("userdata"));
                AutocompleteNonUser(userData.token, userData.id, value)
                  .then(res => {
                    if (res.code === "200") {
                      setOptions(res.data.map(item => item.search_text));
                    }
                  })
                  .catch(console.error);
              }
            }}
            onChange={(_, value) => fetchData(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search Employees"
                size="small"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                }}
                sx={{
                  width: { xs: '100%', sm: '300px' },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'white',
                  }
                }}
              />
            )}
          />
        </Box>

        {/* Table Section */}
        <TableContainer 
          component={Paper}
          sx={{ 
            width: '100%',
            overflow: 'auto',
            mb: 3,
            borderRadius: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
          }}
        >
          {loading ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <CircularProgress sx={{ color: appColors.leaf }} />
            </Box>
          ) : (
            <Table sx={{ minWidth: 800 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>S.NO</StyledTableCell>
                  <StyledTableCell>NAME / ID</StyledTableCell>
                  <StyledTableCell>DATE</StyledTableCell>
                  <StyledTableCell>CHECK IN</StyledTableCell>
                  <StyledTableCell>CHECK OUT</StyledTableCell>
                  <StyledTableCell>PHONE</StyledTableCell>
                  <StyledTableCell>STATUS</StyledTableCell>
                  <StyledTableCell>WORK HOURS</StyledTableCell>
                  <StyledTableCell>ACTIONS</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fetchdatarow.map((row, index) => {
                  const sessions = getSessionsForRow(row);
                  return (
                  <StyledTableRow key={index}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{row.user_name}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#6b7280' }}>ID: {row.user_id}</Typography>
                    </TableCell>
                    <TableCell>{row.date}</TableCell>
                    <TableCell>
                      {sessions.map((s, si) => (
                        <Box key={si} sx={{ whiteSpace: 'nowrap' }}>
                          {s.label && (
                            <Typography component="span" sx={{ fontSize: 11, color: '#6b7280', mr: 0.5 }}>
                              {s.label}:
                            </Typography>
                          )}
                          {s.checkIn || '—'}
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell>
                      {sessions.map((s, si) => (
                        <Box key={si} sx={{ whiteSpace: 'nowrap' }}>
                          {s.checkOut || '—'}
                          {s.isEstimated && s.checkOut && (
                            <Typography component="span" sx={{ fontSize: 10, color: '#6b7280', ml: 0.5 }}>
                              (est.)
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        sx={{
                          bgcolor: row.status === 'active' 
                            ? 'rgba(16, 185, 129, 0.1)' 
                            : 'rgba(239, 68, 68, 0.1)',
                          color: row.status === 'active' 
                            ? '#10b981' 
                            : '#ef4444',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.work_hours}
                      {fullDaySlots(row).total > 0 && (
                        <Typography sx={{ fontSize: 11, color: '#6b7280' }}>of {formatHours(fullDaySlots(row).total)}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Checkout">
                          <IconButton 
                            onClick={() => handleCheckout(row.id)}
                            size="small"
                            sx={{
                              color: appColors.leaf,
                              '&:hover': {
                                bgcolor: 'rgba(31, 122, 84, 0.08)',
                              },
                            }}
                          >
                            <ShoppingCartIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Work Hours">
                          <IconButton
                            onClick={() => fetchWorkHours(row.user_id)}
                            size="small"
                            sx={{
                              color: appColors.leaf,
                              '&:hover': {
                                bgcolor: 'rgba(31, 122, 84, 0.08)',
                              },
                            }}
                          >
                            <AccessTimeIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </StyledTableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
          sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', justifyContent: 'center', px: { xs: 1, sm: 2 } }, '& .MuiTablePagination-actions': { mr: { xs: 0, sm: 8 } } }}
          labelDisplayedRows={({ page: p, count }) =>
            `Page ${p + 1} of ${Math.ceil(count / rowsPerPage)}  |  Total: ${count} records`
          }
        />

        {/* Work Hours Modal */}
        <WorkHoursModal
          open={openWork}
          onClose={() => setOpenWork(false)}
          aria-labelledby="work-hours-modal"
        >
          <ModalContent>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3
            }}>
              <Typography variant="h6" sx={{ color: appColors.leaf, fontWeight: 600 }}>
                Generate Salary
              </Typography>
              <IconButton
                onClick={() => setOpenWork(false)}
                size="small"
                sx={{ color: '#64748b' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {loadingFetch ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress sx={{ color: appColors.leaf }} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Total Work Hours"
                  value={workHours || ''}
                  disabled
                  fullWidth
                />
                <TextField
                  label="Hours Per Day (one full day)"
                  type="number"
                  value={hoursPerDay}
                  onChange={(e) => setHoursPerDay(e.target.value)}
                  helperText={
                    fullDay.parts.length > 0
                      ? `From the slots: ${fullDay.parts.map((p) => `${p.label} ${formatHours(p.hours)}`).join(' + ')} = ${formatHours(fullDay.total)} = 1 day`
                      : 'No slot saved for this person, using 8 hours'
                  }
                  fullWidth
                />
                {(() => {
                  const hours = parseFloat(String(workHours || '').match(/[\d.]+/)?.[0]);
                  const perDay = parseFloat(hoursPerDay);
                  const perSalary = parseFloat(salaryPerDay);
                  if (!hours || !perDay) return null;
                  const days = hours / perDay;
                  return (
                    <Typography sx={{ fontSize: 13, color: '#475569' }}>
                      {formatHours(hours)} worked ÷ {formatHours(perDay)} per day = <strong>{Math.round(days * 100) / 100} days</strong>
                      {perSalary ? ` → ₹${Math.round(days * perSalary).toLocaleString('en-IN')}` : ''}
                    </Typography>
                  );
                })()}
                <TextField
                  label="Salary Per Day"
                  type="number"
                  value={salaryPerDay}
                  onChange={(e) => setSalaryPerDay(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handleGenerateSalary}
                  disabled={!workHours || !hoursPerDay || !salaryPerDay}
                  sx={{
                    bgcolor: appColors.leaf,
                    '&:hover': { bgcolor: appColors.pine },
                    mt: 2
                  }}
                >
                  Generate Salary
                </Button>
              </Box>
            )}
          </ModalContent>
        </WorkHoursModal>

        {/* Snackbar */}
        <AppToast open={openToast} message={snackbarMessage} severity={snackbarSeverity} onClose={handleToastClose} />
      </Box>
  );
}

export default AttendanceEmp;
